import { StageDef } from '../automationMachineInterfaces'
import { INSTAGRAM_AUTOMATION_STAGE_STATUSES } from './instagramAutomationStageStatuses'
import * as instagramUrls from './instagramUrls'
import { InstagramSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import {
  evaluateXpath,
  getCommonAncestor,
  getNearestElementMatch,
} from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'

// Settings property functions
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key) => getIsFulfilledFnFromKey(key, (value) => value)

const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

enum StageKey {
  GATHER_SETTING_TWO_FACTOR_AUTHENTICATION = 'GATHER_SETTING_TWO_FACTOR_AUTHENTICATION',
  FINISH_SETTING_TWO_FACTOR_AUTHENTICATION = 'FINISH_SETTING_TWO_FACTOR_AUTHENTICATION',
}

export async function getAccountCenterModal2faAccountRow(
  platformAccountUsername: string,
) {
  const modal = await getWaitForElementToAppearPromise(
    document,
    (node) => evaluateXpath<HTMLElement>(node, '//div[@role="dialog"]')[0],
    5000,
  )

  const accountNameElement = evaluateXpath<HTMLElement>(
    modal,
    `.//*[contains(text(),"${platformAccountUsername}")]`,
  )[0]

  if (!accountNameElement) {
    throw new Error(
      `Account row with username ${platformAccountUsername} not found`,
    )
  }

  const platformLabel = getNearestElementMatch(
    accountNameElement,
    `*[contains(text(), "Instagram")]`,
    'following',
  )

  if (!platformLabel) {
    throw new Error(`Account row with platform Instagram not found`)
  }

  const row = getCommonAncestor(accountNameElement, platformLabel)

  if (!row) {
    throw Error(
      `Instagram 2fa account row with username ${platformAccountUsername} not found`,
    )
  }

  return row
}

async function getTwoFactorAuthenticationSetting(
  platformAccountUsername: string,
) {
  const accountRow = await getAccountCenterModal2faAccountRow(
    platformAccountUsername,
  )
  await wait(1000)
  accountRow.click()

  const twofaOnHeaderText = await getWaitForElementToAppearPromise(
    document,
    (node) =>
      evaluateXpath<HTMLElement>(
        node,
        '//*[text()="Two-factor authentication is on"]',
      )[0],
    5000,
  )

  if (twofaOnHeaderText) {
    return true
  }
  return false
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_TWO_FACTOR_AUTHENTICATION,
    status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: instagramUrls.accountCenterTwoFactorAuthUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues, platformAccountUsername } = userContext

      const twofaSetting = await getTwoFactorAuthenticationSetting(
        platformAccountUsername,
      )

      await updateStoredInitialValues(
        {
          [InstagramSettingKey.SETUP_TWO_FACTOR_AUTHENTICATION]: twofaSetting,
        },
        enums.InstagramRecommendationKey.INSTAGRAM_SETUP_2FA,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.FINISH_SETTING_TWO_FACTOR_AUTHENTICATION,
    status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
    url: instagramUrls.baseUrl,
    action: async () => true,
  },
]

const updateStageDefs: StageDef[] = []
const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
